import axios from 'axios';
import * as Sentry from '@sentry/react';
import authorizationHeader from '../api/token';
import api from '../api/axios';

const VIMEO_API_BASE = process.env.REACT_APP_VIMEO_API_BASE;
const VIMEO_TOKEN = process.env.REACT_APP_VIMEO_TOKEN;
const DEFAULT_FOLDER_ID = process.env.REACT_APP_VIMEO_DEFAULT_FOLDER_ID;
const VIMEO_USER_ID = process.env.REACT_APP_VIMEO_USER_ID;

export const getVimeoItems = async (folderId = DEFAULT_FOLDER_ID) => {
  try {
    const response = await axios.get(
      `${VIMEO_API_BASE}/users/${VIMEO_USER_ID}/projects/${folderId}/items?sort=alphabetical&per_page=100`,
      {
        headers: {
          Authorization: `bearer ${VIMEO_TOKEN}`,
        },
      }
    );

    if (response.status !== 200) {
      throw new Error(`Error al obtener items de Vimeo: ${response.status} ${response.statusText}`);
    }

    const json = response.data;
    return json || [];
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getVimeoVideoDetails = async videoId => {
  try {
    const response = await axios.get(`${VIMEO_API_BASE}/videos/${videoId}`, {
      headers: {
        Authorization: `Bearer ${VIMEO_TOKEN}`,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        `Error al obtener detalles del video: ${response.status} ${response.statusText}`
      );
    }

    const json = response.data;
    return json;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getVimeoVideoTextTracks = async videoId => {
  try {
    const response = await axios.get(`${VIMEO_API_BASE}/videos/${videoId}/texttracks`, {
      headers: {
        Authorization: `bearer ${VIMEO_TOKEN}`,
      },
    });

    if (response.status !== 200) {
      throw new Error(`Error al obtener texttracks: ${response.status} ${response.statusText}`);
    }

    const data = response.data;
    const activeTrack = data.data?.find(track => track.active);

    if (activeTrack?.link) {
      const vttRes = await axios.get(activeTrack.link);
      const vtt = vttRes.data;
      return vtt;
    }

    return null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const getAllVideosFromFolder = async folderId => {
  const videos = [];

  const processItems = async folderId => {
    try {
      const items = await getVimeoItems(folderId);
      const itemList = items?.data ?? [];
      for (const item of itemList) {
        if (item.type === 'video') {
          const videoId = item.video.uri.split('/')[2];
          videos.push({
            videoId,
            videoName: item.video.name,
          });
        } else if (item.type === 'folder') {
          const subFolderId = item.folder.uri.split('/')[4];
          await processItems(parseInt(subFolderId, 10));
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  await processItems(folderId);
  return videos;
};

export const uploadItemVideosToPinecone = async (item, onProgress = null) => {
  const result = {
    success: true,
    uploaded: 0,
    failed: 0,
    errors: [],
  };

  try {
    let videosToUpload = [];

    if (item.type === 'folder') {
      const folderId = item.folder.uri.split('/')[4];
      videosToUpload = await getAllVideosFromFolder(parseInt(folderId, 10));
    } else if (item.type === 'video') {
      const videoId = item.video.uri.split('/')[2];
      videosToUpload = [
        {
          videoId,
          videoName: item.video.name,
        },
      ];
    } else {
      throw new Error('Tipo de item no válido');
    }

    if (videosToUpload.length === 0) {
      return result;
    }

    for (let i = 0; i < videosToUpload.length; i++) {
      const { videoId, videoName } = videosToUpload[i];

      if (onProgress) {
        onProgress(i + 1, videosToUpload.length, videoName);
      }

      try {
        const [videoDetails, vttContent] = await Promise.all([
          getVimeoVideoDetails(videoId),
          getVimeoVideoTextTracks(videoId),
        ]);

        await upsertVideoVectorToPinecone({
          vimeoId: videoId,
          videoName: videoDetails.name || videoName,
          vttContent: vttContent || '',
          videoDuration: videoDetails.duration || null,
        });

        result.uploaded++;
      } catch (error) {
        result.failed++;
        result.errors.push({
          videoName,
          videoId,
          error: error.message,
        });
        Sentry.captureException(error);
      }
    }

    result.success = result.failed === 0;
    return result;
  } catch (error) {
    Sentry.captureException(error);
    result.success = false;
    result.errors.push({
      error: error.message,
    });
    throw error;
  }
};

export const upsertVideoVectorToPinecone = async ({
  vimeoId,
  videoName,
  vttContent,
  videoDuration = null,
}) => {
  try {
    const headers = await authorizationHeader();
    const response = await api.post(
      '/videos/upsert-vector',
      {
        vimeoId,
        videoName,
        vttContent: vttContent || '',
        videoDuration: videoDuration || null,
      },
      {
        headers,
      }
    );

    if (response.status !== 200) {
      const errorText = response.data.message || 'Error desconocido';
      throw new Error(`Error al subir vectores a Pinecone: ${response.status} ${errorText}`);
    }

    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};
